import type { VehicleStatus, DriverStatus } from '@/types';

/**
 * Validates phone number format
 * Accepts Thai formats like: 081-234-5678, 08-1234-5678, 0812345678
 * Also accepts: +66812345678, +66-81-234-5678, 66812345678
 * The database constraint requires: ^[\+]?[1-9][\d]{0,15}$
 */
export function validatePhoneNumber(phoneNumber: string): boolean {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return false;
  }

  // Remove all non-digit characters except + at the start for validation
  const cleaned = phoneNumber.trim().replace(/[\s\-\(\)\.]/g, '');

  // Get digits only (without +)
  const digitsOnly = cleaned.replace(/\+/g, '');

  // Thai phone numbers: must be exactly 10 digits starting with 0
  // Or 9 digits not starting with 0 (after removing leading 0)
  // International: can be 10-15 digits starting with country code
  if (cleaned.startsWith('0')) {
    // Thai format: 0812345678 or 081-234-5678
    return digitsOnly.length === 10;
  } else if (cleaned.startsWith('+')) {
    // International format: +66812345678
    return digitsOnly.length >= 10 && digitsOnly.length <= 15;
  } else {
    // Without + or 0: 66812345678 or 812345678
    // Accept 9-15 digits (Thai without leading 0, or international without +)
    return digitsOnly.length >= 9 && digitsOnly.length <= 15;
  }
}

/**
 * Normalizes phone number to match database constraint
 * Removes all formatting characters except + at the start
 * Database constraint: ^[\+]?[1-9][\d]{0,15}$
 */
export function normalizePhoneNumber(phoneNumber: string): string {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return phoneNumber;
  }

  // Trim and remove all spaces, dashes, parentheses, dots
  let cleaned = phoneNumber.trim().replace(/[\s\-\(\)\.]/g, '');

  // Handle Thai phone numbers starting with 0
  // Keep the format 0812345678 (10 digits with leading 0)
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    // Keep Thai format with leading 0
    return cleaned;
  }

  // Handle international format
  if (cleaned.startsWith('+')) {
    const afterPlus = cleaned.substring(1);
    if (afterPlus.startsWith('0')) {
      // +0... is invalid, remove the 0
      cleaned = '+' + afterPlus.substring(1);
    }
    return cleaned;
  }

  // For numbers without + or 0, keep as is if valid length
  return cleaned;
}

/**
 * Validates vehicle status
 */
export function validateVehicleStatus(status: string): status is VehicleStatus {
  return status === 'available' || status === 'in_use';
}

/**
 * Validates driver status
 */
export function validateDriverStatus(status: string): status is DriverStatus {
  return status === 'available' || status === 'unavailable';
}

/**
 * Validates seating capacity
 */
export function validateSeatingCapacity(capacity: number): boolean {
  return Number.isInteger(capacity) && capacity > 0 && capacity <= 100;
}

/**
 * Validates registration number format
 */
export function validateRegistrationNumber(registrationNumber: string): boolean {
  if (!registrationNumber || typeof registrationNumber !== 'string') {
    return false;
  }

  // Remove whitespace and check length
  const trimmed = registrationNumber.trim();
  return trimmed.length >= 3 && trimmed.length <= 20;
}

/**
 * Validates full name
 */
export function validateFullName(fullName: string): boolean {
  if (!fullName || typeof fullName !== 'string') {
    return false;
  }

  const trimmed = fullName.trim();
  return trimmed.length >= 2 && trimmed.length <= 100;
}

/**
 * Validates time format (HH:MM)
 */
export function validateTimeFormat(time: string): boolean {
  if (!time || typeof time !== 'string') {
    return false;
  }

  const timePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timePattern.test(time);
}

/**
 * Validates that start time is before end time
 */
export function validateTimeRange(startTime: string, endTime: string): boolean {
  if (!validateTimeFormat(startTime) || !validateTimeFormat(endTime)) {
    return false;
  }

  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  const startMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;

  return startMinutes < endMinutes;
}

/**
 * Form validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates vehicle form data
 */
export function validateVehicleForm(data: {
  registrationNumber: string;
  vehicleType: string;
  brandModel: string;
  seatingCapacity: number;
  status: string;
}): ValidationResult {
  const errors: string[] = [];

  if (!validateRegistrationNumber(data.registrationNumber)) {
    errors.push('Registration number must be between 3 and 20 characters');
  }

  if (!data.vehicleType?.trim()) {
    errors.push('Vehicle type is required');
  }

  if (!data.brandModel?.trim()) {
    errors.push('Brand/Model is required');
  }

  if (!validateSeatingCapacity(data.seatingCapacity)) {
    errors.push('Seating capacity must be a positive integer between 1 and 100');
  }

  if (!validateVehicleStatus(data.status)) {
    errors.push('Vehicle status must be either "available" or "in_use"');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validates driver form data
 */
export function validateDriverForm(data: {
  fullName: string;
  phoneNumber: string;
  status: string;
}): ValidationResult {
  const errors: string[] = [];

  if (!validateFullName(data.fullName)) {
    errors.push('Full name must be between 2 and 100 characters');
  }

  if (!validatePhoneNumber(data.phoneNumber)) {
    errors.push('Phone number must be a valid format with at least 10 digits');
  }

  if (!validateDriverStatus(data.status)) {
    errors.push('Driver status must be either "available" or "unavailable"');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validates schedule entry form data
 */
export function validateScheduleEntryForm(data: {
  usageDate: Date;
  startTime: string;
  endTime: string;
  vehicleId: string;
  driverId: string;
}): ValidationResult {
  const errors: string[] = [];

  if (!data.usageDate || !(data.usageDate instanceof Date) || isNaN(data.usageDate.getTime())) {
    errors.push('Usage date must be a valid date');
  }

  if (!validateTimeFormat(data.startTime)) {
    errors.push('Start time must be in HH:MM format');
  }

  if (!validateTimeFormat(data.endTime)) {
    errors.push('End time must be in HH:MM format');
  }

  if (data.startTime && data.endTime && !validateTimeRange(data.startTime, data.endTime)) {
    errors.push('Start time must be before end time');
  }

  if (!data.vehicleId?.trim()) {
    errors.push('Vehicle selection is required');
  }

  if (!data.driverId?.trim()) {
    errors.push('Driver selection is required');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}